// MOD-SHOWCASE — «Витрина председателя» (E12-T21, ux/08 §13, X-17): три легендарные детали без случайности,
// своя у игрока на неделю. Цена, предел «k из 2» и срок — с сервера (GET /shop/showcase). Тап по карточке →
// MOD-PURCHASE с карточкой предмета; дороже 1 000 талонов — удержание 1 с. Если неделя сменилась, пока окно
// было открыто (`SHOWCASE_EXPIRED`), покупка не проходит: окно перечитывает витрину и объясняет почему.
import { useRef } from 'preact/hooks';
import type { Api, ResponseOf } from '@garages/contract';
import { useApp } from '../app/app.js';
import { RARITY_NAMES, SLOT_NAMES, itemDesc, itemName } from '../content/catalog.js';
import { num } from '../i18n/format.js';
import { t as tr } from '../i18n/t.js';
import { ApiCallError } from '../net/api-client.js';
import { baseRate } from '../pay/flow.js';
import { equipped, online, setInventoryItems, setWallet, shop, wallet } from '../state/game.js';
import type { SheetProps } from '../ui/App.js';
import { Sheet } from '../ui/kit.js';
import { showToast } from '../ui/toast.js';
import { useLoad } from '../ui/use-load.js';
import { Icon } from '../assets/ui.js';
import { buyState, coverPack, inDebt, talonsHint, tn } from './store-model.js';
import { buyLabel, bumpShop, starPacks, useExtras } from './store-goods.js';
import { HoldButton, StatsCompare, TalonPrice, StarsIcon } from './store-ui.js';

type Showcase = ResponseOf<Api['getShowcase']>;
type Pos = Showcase['items'][number];

/** Состояние покупки позиции витрины: «Куплено ✓», предел недели, долг, нехватка (ux/08 §13). */
export function showcaseState(it: Pos, sc: Pick<Showcase, 'weekPurchases' | 'weeklyLimit'>, talons: number, debt: boolean) {
  return buyState({
    priceTalons: it.priceTalons, purchased: it.purchased,
    limit: { per: 'week', total: sc.weeklyLimit, used: it.purchased ? 0 : sc.weekPurchases },
  }, talons, debt);
}

function refreshText(iso: string): string {
  const ms = Date.parse(iso) - Date.now();
  const d = Math.floor(ms / 86_400_000);
  const h = Math.max(0, Math.floor((ms % 86_400_000) / 3_600_000));
  return d > 0 ? tr('CL-SHOWCASE-IN-DAYS', { d, h }) : tr('CL-SHOWCASE-IN-HOURS', { h });
}

export function ShowcaseSheet({ entry }: SheetProps) {
  const app = useApp();
  const load = useLoad(() => app.call('getShowcase'));
  const sc = load.data;
  const w = wallet.value;
  const ex = useExtras(shop.value?.data ?? null);
  const rate = baseRate(starPacks(shop.value?.data ?? null));
  const debt = inDebt(w);
  const changed = !!entry.params['changed'];
  const off = !online.value || load.offline;
  return (
    <Sheet entry={entry} title={tr('SHOP-SHOWCASE-TITLE')}>
      <div class="col" data-testid="MOD-SHOWCASE-body">
        {changed ? <div class="banner warn" data-testid="showcase-changed">{tr('CL-SHOWCASE-CHANGED')}</div> : null}
        <span class="small muted">{tr('SHOP-SHOWCASE-HONEST')}</span>
        {sc ? (
          <div class="row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <span class="badge soft" data-testid="showcase-week">{tr('SHOP-SHOWCASE-WEEK', { k: sc.weekPurchases, limit: sc.weeklyLimit })}</span>
            <span class="small muted" data-testid="showcase-refresh">{tr('CL-SHOWCASE-REFRESH')} · {refreshText(sc.refreshAt)}</span>
          </div>
        ) : null}
        {debt ? <div class="banner warn">{tr('SHOP-DEBT')}</div> : null}
        {load.error && !load.offline ? <div class="banner" role="alert">{tr('CL-STORE-SHOWCASE-ERR')}<span class="spacer" /><button class="btn small outline" onClick={load.reload}>{tr('CL-UI-KIT-15')}</button></div> : null}
        {off ? <div class="banner">{tr('CL-SCREENS-STORE-04')}</div> : null}
        <div class="showcase-cards">
          {!sc && load.loading ? [0, 1, 2].map((i) => <div key={i} class="skeleton" style={{ height: 150 }} />) : null}
          {sc?.items.map((it) => {
            const st = showcaseState(it, sc, w?.talons ?? 0, debt);
            const lab = st.kind === 'limit' ? { text: tr('CL-STORE-AGAIN-MONDAY'), disabled: true } : buyLabel(st);
            const worn = equipped.value.find((e) => e.slot === it.slot);
            return (
              <button key={it.pos} class={`card showcase-card ${it.purchased ? 'bought' : ''} ${off ? 'off' : ''}`} data-testid={`showcase-${it.pos}`} disabled={off}
                onClick={() => { app.haptic('selection'); app.nav.openSheet('MOD-PURCHASE', { showcase: it, weekKey: sc.weekKey, weekPurchases: sc.weekPurchases, weeklyLimit: sc.weeklyLimit }); }}>
                <span class="item-tile rarity-legendary sc-icon"><span class="glyph"><Icon id={it.defId} fallback="🧰" size={44} /></span></span>
                <span class="col" style={{ gap: 2, flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <b>{itemName(it.defId)}</b>
                  <span class="small rarity-legendary" style={{ color: 'var(--r)' }}>{RARITY_NAMES.legendary} · {SLOT_NAMES[it.slot]} · {tr('CL-SHOWCASE-LEVEL', { n: it.itemLevel })}</span>
                  <span class="small muted">{tr('CL-SHOWCASE-ROLL')}</span>
                  {it.stats ? <StatsCompare stats={it.stats} slot={it.slot} compact testId={`showcase-stats-${it.pos}`} /> : worn ? <span class="small muted">{tr('CL-SHOWCASE-WORN', { name: itemName(worn.defId) })}</span> : null}
                  {it.purchased ? <span class="pos small" data-testid={`showcase-bought-${it.pos}`}>{tr('SHOP-SHOWCASE-BOUGHT')}</span>
                    : <TalonPrice n={it.priceTalons} hint={talonsHint(it.priceTalons, rate, ex.rubPerStar)} />}
                </span>
                {!it.purchased ? <span class={`btn small ${lab.disabled || off ? 'disabled' : 'accent'} sc-btn`}>{off ? tr('UI-OFFLINE') : st.kind === 'ok' ? tr('CL-SHOWCASE-TAKE') : lab.text}</span> : null}
              </button>
            );
          })}
        </div>
        <p class="small muted" style={{ margin: 0 }}>{tr('CL-SHOWCASE-NOTE')}</p>
      </div>
    </Sheet>
  );
}

/** MOD-PURCHASE для позиции витрины: карточка предмета как MOD-ITEM, цена с ориентиром, удержание 1 с (§2.2). */
export function ShowcasePurchaseBody(p: { entryParams: Record<string, unknown> }) {
  const app = useApp();
  const it = p.entryParams['showcase'] as Pos;
  const weekKey = String(p.entryParams['weekKey']);
  const sc = { weekPurchases: Number(p.entryParams['weekPurchases'] ?? 0), weeklyLimit: Number(p.entryParams['weeklyLimit'] ?? 2) };
  const w = wallet.value;
  const packs = starPacks(shop.value?.data ?? null);
  const rate = baseRate(packs);
  const ex = useExtras(shop.value?.data ?? null);
  const st = showcaseState(it, sc, w?.talons ?? 0, inDebt(w));
  const lab = st.kind === 'limit' ? { text: tr('CL-STORE-AGAIN-MONDAY'), disabled: true } : buyLabel(st);
  const cover = st.kind === 'lack' ? coverPack(st.deficit, packs.filter((x) => !x.blocked)) : null;
  const key = useRef(app.idem());
  const buy = async () => {
    try {
      const r = await app.call('buyShowcase', { params: { pos: it.pos }, body: { weekKey }, idempotencyKey: key.current });
      key.current = app.idem();
      setWallet(r.wallet);
      setInventoryItems((xs) => [...xs, r.item]);
      bumpShop();
      app.haptic('success');
      app.nav.openSheet('MOD-REWARD', { grant: { items: [{ defId: r.item.defId, qty: 1 }] } }, { replace: true });
    } catch (e) {
      const code = e instanceof ApiCallError ? e.code : 'INTERNAL';
      if (code !== 'NETWORK' && code !== 'TIMEOUT') key.current = app.idem();
      app.haptic('error');
      if (code === 'SHOWCASE_EXPIRED') { bumpShop(); app.nav.openSheet('MOD-SHOWCASE', { changed: true }, { replace: true }); return; }
      const t: Record<string, string> = { NOT_ENOUGH_TALONS: 'CL-STORE-ERR-TALONS', WEEKLY_LIMIT: 'CL-SHOWCASE-ERR-WEEK', ALREADY_PURCHASED: 'CL-STORE-ERR-BOUGHT', TALONS_DEBT: 'SHOP-DEBT' };
      if (t[code]) { showToast(tr(t[code]!), 'error'); if (code === 'NOT_ENOUGH_TALONS') void app.call('getWallet').then(setWallet).catch(() => {}); bumpShop(); return; }
      if (code === 'INTERNAL' || code === 'NETWORK' || code === 'TIMEOUT') { showToast(tr('CL-STORE-ERR-SAFE'), 'error'); return; }
      app.handleError(e, { resource: tr('CL-STORE-TALONS-SAFE') });
    }
  };
  const toPack = () => { if (cover) app.nav.openSheet('MOD-PURCHASE', { product: cover.pack, rate, returnTo: { id: 'MOD-PURCHASE', params: p.entryParams } }); };
  return (
    <div class="col" data-testid="purchase-showcase">
      <div class="row">
        <div class="item-tile rarity-legendary" style={{ width: 96, flexShrink: 0 }}><span class="glyph"><Icon id={it.defId} fallback="🧰" size={56} /></span></div>
        <div class="col" style={{ gap: 2 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>{itemName(it.defId)}</h2>
          <span class="rarity-legendary" style={{ color: 'var(--r)' }}>{RARITY_NAMES.legendary} · {SLOT_NAMES[it.slot]}</span>
          <span class="small muted">{itemDesc(it.defId)}</span>
        </div>
      </div>
      {it.stats ? <StatsCompare stats={it.stats} slot={it.slot} testId="showcase-stats" /> : null}
      <div class="card col" style={{ gap: 4 }}>
        <span>{tr('CL-SHOWCASE-LEVEL', { n: it.itemLevel })} · {tr('CL-SHOWCASE-ROLL')}</span>
        <span class="small muted">{tr('SHOP-SHOWCASE-HONEST')}</span>
      </div>
      <div class="col" style={{ alignItems: 'center', textAlign: 'center' }}>
        <TalonPrice n={it.priceTalons} hint={talonsHint(it.priceTalons, rate, ex.rubPerStar)} />
        <span class="small muted">{tr('CL-STORE-HAVE', tn(w?.talons ?? 0))}</span>
        {cover ? <button class="banner lack-line" data-testid="lack-stars" onClick={toPack}>{tr('CL-STORE-LACK-STARS', { n: num(cover.stars) })}<StarsIcon size={14} /></button> : null}
        <div class="purchase-cta">
          {st.kind === 'lack'
            ? <HoldButton text={lab.text} hold={false} disabled={!online.value || !cover} testId="buy-showcase" onDone={toPack} />
            : <HoldButton text={!online.value ? tr('UI-OFFLINE') : lab.text} hold={st.kind === 'ok' && st.hold} disabled={!online.value || lab.disabled} testId="buy-showcase" onDone={buy} />}
        </div>
        <p class="small muted" style={{ margin: 0 }}>{tr('CL-STORE-TALON-NOTE')}</p>
      </div>
    </div>
  );
}

